import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable } from '@/components/shared/DataTable';
import { useRooms } from '@/hooks/useRooms';
import { useRoomTypes } from '@/hooks/useRoomTypes';
import { formatDate } from '@/utils/format';
import { toast } from 'sonner';

/**
 * Room Closures Management Component
 * Manage room closures and maintenance periods
 */
export const RoomClosuresManagement = ({ API }) => {
  const { rooms } = useRooms(API);
  const { roomTypes } = useRoomTypes(API);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [closureForm, setClosureForm] = useState({
    closure_type: 'specific', // 'specific', 'type', 'all'
    room_ids: [],
    room_type_id: '',
    start_date: '',
    end_date: '',
    reason: 'Closed for maintenance'
  });

  // Fetch closures on mount
  React.useEffect(() => {
    fetchClosures();
  }, []);

  const fetchClosures = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/room-closures`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Infer closure_type from the data
        const closuresWithType = data.map(closure => {
          let closure_type = 'specific';
          if (closure.room_type_id && (!closure.room_ids || closure.room_ids.length === 0)) {
            closure_type = 'type';
          } else if (!closure.room_type_id && closure.room_ids && closure.room_ids.length === rooms.length) {
            closure_type = 'all';
          }
          return { ...closure, closure_type };
        });
        setClosures(closuresWithType);
      }
    } catch (error) {
      console.error('Error fetching room closures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validation
    if (closureForm.closure_type === 'specific' && closureForm.room_ids.length === 0) {
      toast.error('Please select at least one room');
      return;
    }
    if (closureForm.closure_type === 'type' && !closureForm.room_type_id) {
      toast.error('Please select a room type');
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare payload based on closure type
      const payload = {
        room_ids: closureForm.room_ids,
        room_type_id: closureForm.room_type_id,
        start_date: closureForm.start_date,
        end_date: closureForm.end_date,
        reason: closureForm.reason
      };
      
      const response = await fetch(`${API}/admin/room-closures`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const message = closureForm.closure_type === 'all' 
          ? 'All rooms closed for selected dates'
          : closureForm.closure_type === 'type'
          ? 'All rooms of selected type closed'
          : `${closureForm.room_ids.length} room(s) closed`;
        toast.success(message);
        await fetchClosures();
        setShowDialog(false);
        resetForm();
      } else {
        toast.error('Failed to create room closure');
      }
    } catch (error) {
      console.error('Error creating room closure:', error);
      toast.error('Error creating room closure');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (closureId) => {
    if (!window.confirm('Are you sure you want to delete this room closure?')) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/room-closures/${closureId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        toast.success('Room closure deleted');
        await fetchClosures();
      } else {
        toast.error('Failed to delete room closure');
      }
    } catch (error) {
      console.error('Error deleting room closure:', error);
      toast.error('Error deleting room closure');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setClosureForm({
      closure_type: 'specific',
      room_ids: [],
      room_type_id: '',
      start_date: '',
      end_date: '',
      reason: 'Closed for maintenance'
    });
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return 'Unknown';
    
    const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
    const roomTypeName = roomType ? roomType.name : 'Unknown Type';
    
    return `Room ${room.room_number} - ${roomTypeName}`;
  };

  const getClosureDescription = (closure) => {
    if (closure.closure_type === 'all') {
      return 'All Rooms';
    } else if (closure.closure_type === 'type' && closure.room_type_id) {
      const roomType = roomTypes.find(rt => rt.id === closure.room_type_id);
      const count = rooms.filter(r => r.room_type_id === closure.room_type_id).length;
      return `${roomType?.name || 'Unknown Type'} (${count} rooms)`;
    } else if (closure.room_ids && closure.room_ids.length > 0) {
      if (closure.room_ids.length > 2) {
        // Show first room and count for multiple selections
        const firstRoom = getRoomName(closure.room_ids[0]);
        return `${firstRoom} +${closure.room_ids.length - 1} more`;
      }
      return closure.room_ids.map(id => getRoomName(id)).join(', ');
    }
    return 'Unknown';
  };

  const columns = [
    {
      key: 'closure_type',
      label: 'Type',
      render: (value, closure) => (
        <div className="text-sm">
          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
            value === 'all' ? 'bg-red-100 text-red-800' :
            value === 'type' ? 'bg-orange-100 text-orange-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {value === 'all' ? 'All Rooms' : value === 'type' ? 'Room Type' : 'Specific'}
          </span>
        </div>
      )
    },
    {
      key: 'room_ids',
      label: 'Rooms/Type',
      render: (value, closure) => {
        const description = getClosureDescription(closure);
        const hasMultiple = closure.room_ids && closure.room_ids.length > 2;
        
        return (
          <div className="text-sm text-gray-900">
            {description}
            {hasMultiple && (
              <div className="text-xs text-gray-500 mt-1">
                {closure.room_ids.map(id => {
                  const room = rooms.find(r => r.id === id);
                  const roomType = roomTypes.find(rt => rt.id === room?.room_type_id);
                  return room ? `Room ${room.room_number} - ${roomType?.name || 'Unknown'}` : null;
                }).filter(Boolean).slice(0, 5).join(', ')}
                {closure.room_ids.length > 5 && '...'}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'start_date',
      label: 'Start Date',
      render: (value) => <div className="text-sm text-gray-900">{formatDate(value)}</div>
    },
    {
      key: 'end_date',
      label: 'End Date',
      render: (value) => <div className="text-sm text-gray-900">{formatDate(value)}</div>
    },
    {
      key: 'reason',
      label: 'Reason',
      render: (value) => <div className="text-sm text-gray-600">{value}</div>
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (_, closure) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(closure.id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Room Closures</h2>
          <p className="text-sm text-gray-600 mt-1">
            {closures.length} closure{closures.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Closure
        </Button>
      </div>

      {/* Closures Table */}
      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={closures}
            testIdPrefix="closure"
            emptyState={
              <div className="text-center text-gray-500 py-12">
                <p>No room closures scheduled</p>
              </div>
            }
          />
        </CardContent>
      </Card>

      {/* Create Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Add Room Closure</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closure Type
                </label>
                <select
                  value={closureForm.closure_type}
                  onChange={(e) => setClosureForm({ 
                    ...closureForm, 
                    closure_type: e.target.value,
                    room_ids: [],
                    room_type_id: ''
                  })}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="specific">Specific Room(s)</option>
                  <option value="type">All Rooms of a Type</option>
                  <option value="all">All Rooms</option>
                </select>
              </div>

              {closureForm.closure_type === 'specific' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Rooms
                  </label>
                  <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                    {rooms.map((room) => {
                      const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
                      return (
                        <label key={room.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                          <input
                            type="checkbox"
                            checked={closureForm.room_ids.includes(room.id)}
                            onChange={(e) => {
                              const newRoomIds = e.target.checked
                                ? [...closureForm.room_ids, room.id]
                                : closureForm.room_ids.filter(id => id !== room.id);
                              setClosureForm({ ...closureForm, room_ids: newRoomIds });
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm flex-1">
                            <span className="font-medium">Room {room.room_number}</span>
                            <span className="text-gray-600"> - {roomType?.name || 'Unknown Type'}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {closureForm.room_ids.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">Please select at least one room</p>
                  )}
                  {closureForm.room_ids.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      {closureForm.room_ids.length} room{closureForm.room_ids.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              {closureForm.closure_type === 'type' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Room Type
                  </label>
                  <select
                    value={closureForm.room_type_id}
                    onChange={(e) => setClosureForm({ ...closureForm, room_type_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Select a room type</option>
                    {roomTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({rooms.filter(r => r.room_type_id === type.id).length} rooms)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {closureForm.closure_type === 'all' && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">
                    ⚠️ This will close <strong>all rooms</strong> for the selected date range.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={closureForm.start_date}
                  onChange={(e) => setClosureForm({ ...closureForm, start_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={closureForm.end_date}
                  onChange={(e) => setClosureForm({ ...closureForm, end_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <input
                  type="text"
                  value={closureForm.reason}
                  onChange={(e) => setClosureForm({ ...closureForm, reason: e.target.value })}
                  placeholder="e.g., Maintenance, Renovation"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
